import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import TextInputField from '@/components/inputs/TextInputField';
import {
  ManualGooglePlaceData,
  manualGooglePlaceDetailResolver
} from '@/lib/validations/ManualGooglePlaceDetailSchema';
import { i18n } from '@/utils/i18n';

interface ManualGooglePlaceFormProps {
  initialData?: ManualGooglePlaceData;
  variant?: 'default' | 'rounded_with_shadow' | 'gray_borders';
  onDone?: (data: ManualGooglePlaceData) => void;
}

const ManualGooglePlaceForm = ({ initialData, variant = 'default', onDone }: ManualGooglePlaceFormProps) => {
  const { control, handleSubmit, watch, trigger, reset, setFocus } = useForm<ManualGooglePlaceData>({
    defaultValues: {
      ...initialData,
      country: 'USA'
    },
    mode: 'onChange',
    resolver: manualGooglePlaceDetailResolver
  });

  const onSubmit: SubmitHandler<ManualGooglePlaceData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  const values = watch();

  useEffect(() => {
    const allFilled =
      values.address1?.trim() &&
      values.city?.trim() &&
      values.region?.trim() &&
      values.postalCode?.trim() &&
      values.country?.trim();

    if (allFilled) {
      trigger().then((valid) => {
        if (valid) handleSubmit(onSubmit)();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.address1, values.city, values.region, values.postalCode, values.country]);

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    } else {
      reset({
        address1: '',
        city: '',
        region: '',
        postalCode: '',
        country: 'USA'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.inputsContainer}>
      <TextInputField
        name="address1"
        control={control}
        placeholder={i18n.t('manual_address.address1_placeholder')}
        variant={variant}
        returnKeyLabel="next"
        returnKeyType="next"
        onSubmitEditing={() => setFocus('city')}
      />

      <TextInputField
        name="city"
        control={control}
        placeholder={i18n.t('manual_address.city_placeholder')}
        variant={variant}
        returnKeyLabel="next"
        returnKeyType="next"
        onSubmitEditing={() => setFocus('region')}
      />

      <TextInputField
        name="region"
        control={control}
        placeholder={i18n.t('manual_address.region_placeholder')}
        variant={variant}
        returnKeyLabel="next"
        returnKeyType="next"
        onSubmitEditing={() => setFocus('postalCode')}
      />

      <TextInputField
        name="postalCode"
        control={control}
        placeholder={i18n.t('manual_address.postalCode_placeholder')}
        variant={variant}
        type="numeric-string"
        returnKeyLabel="done"
        returnKeyType="done"
        onSubmitEditing={handleSubmit(onSubmit)}
      />

      <TextInputField name="country" control={control} placeholder="USA" editable={false} variant={variant} />
    </View>
  );
};

const styles = StyleSheet.create({
  inputsContainer: {
    flex: 1,
    gap: 5
  }
});

export default ManualGooglePlaceForm;
